import { decreaseId, increaseId, sortKeys } from './utils/sortKeys';
import { copy, copyStrict } from './utils/copy';
import isEqual from 'lodash.isequal';
import { lastMigrationVersion } from './migration';
import { CharSlotId, StepId, TimelineId, FilterPreset, RightOrLeft, ChoiceId, EditedBy } from './types';
import { IStory, IChoice, IStoryMetadata, ICharSlots, ICharSlot, ITimeline, IStep, ICharMetadata, ISteps, IBubble, IGesture, IChoices, ICamera, IGestures, IBackground, IBgLayer, IBookmark, IBookmarks } from './validation';

const storyConfig = {
  /** @deprecated maxChars to be removed with migration14 */
  maxChars: 7,
  // This is only for the initial position
  slotsLayout: {
    cs1: {
      x: -225.85651537335286,
      y: 379.21156661786245,
      dir: 1,
    },
    cs2: {
      x: 11.332357247437812,
      y: 384.1874084919473,
      dir: 1,
    },
    cs3: {
      x: 240.61493411420201,
      y: 376.2811127379209,
      dir: -1,
    },
    cs4: {
      x: 240.61493411420201,
      y: 376.2811127379209,
      dir: -1,
    },
    cs5: {
      x: 240.61493411420201,
      y: 376.2811127379209,
      dir: -1,
    },
    cs6: {
      x: 240.61493411420201,
      y: 376.2811127379209,
      dir: -1,
    },
    cs7: {
      x: 240.61493411420201,
      y: 376.2811127379209,
      dir: -1,
    },
  } as Record<CharSlotId, { x: number; y: number; dir: number }>,
  maxChoiceOptions: 3,
  firstMigrationVersion: 1,
};

const getSlotDefaultConfig = (slotId: CharSlotId) => {
  const layoutConfig = storyConfig.slotsLayout[slotId];

  if (!layoutConfig) {
    throw new Error(
      `Error (getSlotDefaultConfig): Cannot find config for slotId '${slotId}'`,
    );
  }

  return {
    id: slotId,
    disabledOnStart: false,
    layout: {
      x: layoutConfig.x,
      y: layoutConfig.y,
      dir: layoutConfig.dir,
      scale: 0.8,
      zIndex: 0,
      layer: 0,
    },
    charMetadata:
      'https://cms.characters.alpha.pnk.one/defaultcast/0xED5AF388653567Af2F388E6224dC7C4b3241C544/6352/metadata.json',
  };
};

interface Diff {
  same?: Record<string, any>;
  diff?: Record<string, { old: any; new: any }>;
}

const getDiff = (
  prevVal?: Record<string, any>,
  newVal?: Record<string, any>,
): Diff => {
  if (!prevVal || !newVal) {
    // If they are equally undefined, no diff
    if (!prevVal && !newVal) {
      return {
        same: prevVal,
        diff: undefined,
      };
    }
    return {
      same: undefined,
      diff: {
        old: prevVal,
        new: newVal,
      } as { old: any; new: any },
    };
  }

  return [...new Set([...Object.keys(prevVal), ...Object.keys(newVal)])].reduce(
    (res, cur) => {
      const val1 = prevVal[cur];
      const val2 = newVal[cur];

      const isSame = isEqual(val1, val2);

      if (isSame) {
        return {
          ...res,
          same: {
            ...res.same,
            [cur]: val1,
          },
        };
      }

      return {
        ...res,
        diff: {
          ...res.diff,
          [cur]: {
            old: val1,
            new: val2,
          },
        },
      };
    },
    {} as Diff,
  );
};

/**
 * Steps are linear
 * Timelines are not, once a timeline is removed, we do not reindex the obj keys
 *
 * Every object GET must be wrapped in `copy` so it's readonly
 */

export enum StoryEvents {
  SLOTS_UPDATE = 'slots',
  FILTER_UPDATE = 'filter',
  TIMELINE_NAVIGATION = 'timeline navigation',
  STEP_NAVIGATION = 'step navigation',
}

export interface DetachListener {
  detach: () => void;
}

export const INITIAL_STEP = 's1';
export const INITIAL_TIMELINE = 't1';

export type TimelineType = 'merge' | 'choice' | 'root';

export interface TimelinesMetadata extends Omit<ITimeline, 'steps'> {
  id: TimelineId;
  type: TimelineType;
  stepsCount: number;
}

type Steps = Record<StepId, StepClass>;

interface STProps {
  timelineId?: TimelineId;
  stepId?: StepId;
}

export class StoryTeller {
  private story: IStory;

  private _timelineId: TimelineId;

  private _stepId: StepId;

  private _steps: Steps;

  // @TODO: Do we need to keep this state to handle the data? I feel like this belongs to the game
  private _selectedChoice?: IChoice;

  private eventHandlers: Partial<
    Record<StepEvents | StoryEvents, { id: number; fn: (p: any) => void }[]>
  >;

  private _onStoryUpdate?: (story: IStory) => void;

  constructor(props?: STProps) {
    this.story = this.getDefaultStory();

    this._timelineId = props?.timelineId || INITIAL_TIMELINE;
    this._stepId = props?.stepId || INITIAL_STEP;
    this._steps = {};
    this.eventHandlers = {};
    this._selectedChoice = undefined;
  }

  get isRootTimeline(): boolean {
    return this._timelineId === INITIAL_TIMELINE;
  }

  get isChoiceTimeline(): boolean {
    return Boolean(this.selectedChoice);
  }

  get isMergeTimeline(): boolean {
    return Object.values(this.story.timelines).some(
      (timeline) => timeline.mergeTimeline === this.timelineId,
    );
  }

  get isFirstStep(): boolean {
    return this._stepId === INITIAL_STEP;
  }

  get isLastStep(): boolean {
    return this._stepId === this.lastStepId;
  }

  get timelineId(): TimelineId {
    return this._timelineId;
  }
  private set stepId(id: StepId) {
    this._stepId = id;
  }
  get stepId(): StepId {
    return this._stepId;
  }

  get slotIds(): CharSlotId[] {
    return sortKeys(Object.keys(this.story.charSlots)) as CharSlotId[];
  }

  get stepsCount(): number {
    return this.stepIds.length;
  }

  get linearStepIndex(): number {
    return this.stepIds.indexOf(this.stepId);
  }

  get metadata(): IStoryMetadata {
    // return a copy so if the user changes the values from within metadata, doesnt affect the class instance
    return copyStrict(this.story.metadata);
  }

  get lastStep(): StepId {
    return this.timeline.lastStep;
  }

  // @TODO: Ideally dont expose this
  get slots(): ICharSlots {
    return copyStrict(this.story.charSlots);
  }

  get filterPreset(): FilterPreset {
    return this.story.filterPreset;
  }

  private get timelineIds(): TimelineId[] {
    return sortKeys(Object.keys(this.story.timelines)) as TimelineId[];
  }

  private get lastTimelineId(): TimelineId {
    return this.timelineIds[this.timelineIds.length - 1];
  }

  private get timeline(): ITimeline {
    return this.story.timelines[this.timelineId];
  }

  public get step(): StepClass {
    return this._steps[this.stepId];
    // return this.timeline.steps[this.stepId];
  }

  // @TODO: Ideally dont expose this
  public get steps(): Steps {
    return this._steps;
  }

  private get firstStep(): IStep | undefined {
    return this.timeline.steps.s1;
  }

  // @TODO: Ideally dont expose this
  public get stepIds(): StepId[] {
    return sortKeys(Object.keys(this.steps)) as StepId[];
  }

  private get lastStepId(): StepId {
    return this.stepIds[this.stepIds.length - 1];
  }

  get charIds(): string[] {
    return this.slotIds.map((id) => this.getSlot(id).charMetadata.id);
  }

  get selectedChoice(): IChoice | undefined {
    return this._selectedChoice;
  }

  get revealedTimeline(): TimelineId | undefined {
    return this.story.revealedTimelineId;
  }

  get bookmarks(): IBookmarks {
    return this.story.bookmarks || [];
  }

  get query() {
    return {
      isSlotEnabled: (id: CharSlotId) => {
        if (!this.slots[id]) {
          throw new Error(
            `Error (isSlotEnabled): Could not find slot with id '${id}'.`,
          );
        }
        return !this.slots[id].disabledOnStart;
      },
      isTalking: (id: CharSlotId) => {
        if (!this.slots[id]) {
          throw new Error(
            `Error (isTalking): Could not find slot with id '${id}'.`,
          );
        }
        return this.step.bubble?.charSlotId === id;
      },
      charDir: (id: CharSlotId) => {
        if (!this.slots[id]) {
          throw new Error(
            `Error (charDir): Could not find slot with id '${id}'.`,
          );
        }
        return (this.getStepGesture(id)?.dirX ||
          this.slots[id].layout.dir) as RightOrLeft;
      },
    };
  }

  /*
    We need to emit off the events once the listeners are set
    force setting step since this is so the games reads initial state
    because we set storyTeller data prior to loading the game part
  */
  init() {
    this.storyEventHandler(StoryEvents.FILTER_UPDATE);
    this.storyEventHandler(StoryEvents.TIMELINE_NAVIGATION);
    this.storyEventHandler(StoryEvents.STEP_NAVIGATION);
    this.storyEventHandler(StoryEvents.SLOTS_UPDATE);

    this.stepEventHandler(StepEvents.BUBBLE_UPDATE, this.step, true);
    this.stepEventHandler(StepEvents.GESTURES_UPDATE, this.step, true);
    this.stepEventHandler(StepEvents.CAMERA_UPDATE, this.step, true);
    this.stepEventHandler(StepEvents.BACKGROUND_UPDATE, this.step, true);
    this.stepEventHandler(StepEvents.CHOICES_UPDATE, this.step, true);
  }

  setEventListener(fn: (story: IStory) => void) {
    this._onStoryUpdate = fn;
  }

  getTimelinesMetadata(): TimelinesMetadata[] {
    const toTimelinesList: TimelineId[] = [];
    return this.timelineIds.map((id) => {
      const timeline = this.story.timelines[id];
      if (timeline.toTimeline) {
        toTimelinesList.push(timeline.toTimeline);
      }
      const type =
        id === INITIAL_TIMELINE
          ? 'root'
          : timeline.mergeTimeline || toTimelinesList.includes(id)
          ? 'merge'
          : 'choice';
      const { steps, ...rest } = timeline;
      return {
        ...rest,
        id,
        type,
        stepsCount: Object.keys(steps).length,
      };
    });
  }

  getChar(id: CharSlotId): ICharSlot {
    return this.slots[id];
  }

  goToCliffhanger(): StoryTeller {
    if (!this.revealedTimeline) {
      console.error(`Error (goToCliffhanger): No 'revealedTimeline' set.`);
      return this;
    }
    // Go to the last revealed timeline
    this.goToTimeline(this.revealedTimeline);
    // then go to the last step
    this.goToStep(this.lastStep);
    return this;
  }

  goToNextStep(): StoryTeller {
    if (!this.step.nextStep) {
      if (this.timeline.mergeTimeline) {
        this.goToTimeline(this.timeline.mergeTimeline);
        return this;
      }
      console.error(
        `Error (goToNextStep): Trying to go to next step from last step. Skipping...`,
      );
      return this;
    }
    this.goToStep(this.step.nextStep);
    return this;
  }

  goToPrevStep(): StoryTeller {
    if (!this.step.prevStep) {
      console.error(
        `Error (goToPrevStep): Trying to go to previous step from first step. Skipping...`,
      );
      return this;
    }
    this.goToStep(this.step.prevStep);
    return this;
  }

  goToStep(id: StepId): StoryTeller {
    if (!this.steps[id]) {
      throw new Error(`Error (goToStep): Step with id '${id}' does not exist.`);
    }
    this.stepId = id;
    this.storyEventHandler(StoryEvents.STEP_NAVIGATION);
    return this;
  }

  goToStepByIndex(index: number): StoryTeller {
    if (!this.stepIds[index]) {
      throw new Error(
        `Error (goToStepByIndex): Not a valid index '${index}'. (Steps length: ${this.stepsCount}).`,
      );
    }
    this.stepId = this.stepIds[index];
    this.storyEventHandler(StoryEvents.STEP_NAVIGATION);
    return this;
  }

  selectChoice(id: ChoiceId) {
    const choice = this.step.choices?.[id];
    if (!choice) {
      throw new Error(
        `Error (selectChoice): Cannot find choice with id '${id}'.`,
      );
    }
    this.story.choicesMade[this._timelineId] = id;
    this.goToTimeline(choice.toTimeline, choice);
  }

  goToMergeTimeline(): StoryTeller {
    if (!this.timeline.toTimeline) {
      console.error(
        `Error (goToMergeTimeline): Current timeline doenst have a 'toTimeline'. Skipping...`,
      );
      return this;
    }
    this.goToTimeline(this.timeline.toTimeline);
    return this;
  }

  private goToTimeline(id: TimelineId, choice?: IChoice): StoryTeller {
    if (!this.story.timelines[id]) {
      console.error(
        `Error (gotToTimeline): Could not find timeline with id '${id}'. Skipping...`,
      );
      return this;
    }
    // Reset step marker before moving timelines to prevent undefined step
    this.stepId = INITIAL_STEP;
    this._selectedChoice = choice;
    this._timelineId = id;
    this.updateSteps();
    this.storyEventHandler(StoryEvents.TIMELINE_NAVIGATION);
    return this;
  }

  goToPrevChoiceTimeline(): StoryTeller {
    if (!this.isMergeTimeline) {
      console.error(
        `Error (goToPrevChoiceTimeline): Can only be callen from a merge timeline. Skipping...`,
      );
      return this;
    }
    const choiceId = this.story.choicesMade[this.timeline.root];
    this.goToTimeline(this.timeline.root);
    this.goToStep(this.timeline.lastStep);
    this.selectChoice(choiceId);
    this.goToStep(this.timeline.lastStep);
    return this;
  }

  goToPrevTimeline(): StoryTeller {
    if (this.timelineId === INITIAL_TIMELINE) {
      console.error(
        `Error (goToPrevTimeline): Cannot go up from root timeline. Skipping...`,
      );
      return this;
    }
    this.goToTimeline(this.timeline.root);
    this.goToStep(this.lastStepId);
    return this;
  }

  disableSlot(id: CharSlotId): StoryTeller {
    if (!this.story.charSlots[id]) {
      console.error(
        `Error (disableSlot): Cannot find slot with id '${id}'. Skipping...`,
      );
      return this;
    }
    const prevCharSlots = copy(this.story.charSlots);
    this.story.charSlots[id].disabledOnStart = true;
    this.storyEventHandler(
      StoryEvents.SLOTS_UPDATE,
      getDiff(prevCharSlots, this.story.charSlots),
    );
    return this;
  }

  enableSlot(id: CharSlotId): StoryTeller {
    if (!this.story.charSlots[id]) {
      console.error(
        `Error (enableSlot): Cannot find slot with id '${id}'. Skipping...`,
      );
      return this;
    }
    const prevCharSlots = copy(this.story.charSlots);
    this.story.charSlots[id].disabledOnStart = false;
    this.storyEventHandler(
      StoryEvents.SLOTS_UPDATE,
      getDiff(prevCharSlots, this.story.charSlots),
    );
    return this;
  }

  getSlot(id: CharSlotId): Readonly<ICharSlot> {
    const slot = this.story.charSlots[id];
    if (!slot) {
      throw new Error(`Error (slot): Cannot find slot with id '${id}'.`);
    }
    return copyStrict(slot);
  }

  updateSlotName(id: CharSlotId, name: string) {
    if (!this.story.charSlots[id]) {
      throw new Error(`Error (slot): Cannot find slot with id '${id}'.`);
    }
    const prevCharSlots = copy(this.story.charSlots);
    this.story.charSlots[id].displayName = name;
    this.storyEventHandler(
      StoryEvents.SLOTS_UPDATE,
      getDiff(prevCharSlots, this.story.charSlots),
    );
    return this;
  }

  setFilter(filter: FilterPreset): StoryTeller {
    this.story.filterPreset = filter;

    this.storyEventHandler(StoryEvents.FILTER_UPDATE);
    return this;
  }

  setVersion(version: number): StoryTeller {
    if (version > lastMigrationVersion) {
      console.error(
        `Error: Cannot set version '${version}' higher than last migration version '${lastMigrationVersion}'. Skipping...`,
      );
      return this;
    }
    if (version < storyConfig.firstMigrationVersion) {
      console.error(
        `Error: Cannot set version '${version}' lower than first migration version '${storyConfig.firstMigrationVersion}'. Skipping...`,
      );
      return this;
    }
    this.story.metadata.version = version;
    return this;
  }

  save(editedBy: EditedBy): StoryTeller {
    this.story.metadata.timestamp = Date.now();
    this.story.metadata.saveCount += 1;
    this.story.metadata.editedBy = editedBy;
    return this;
  }

  getUnusedSlotId(): CharSlotId {
    const slotIds = this.slotIds;

    const idsTaken = new Array(slotIds.length + 1).fill(false);
    for (let i = 0; i < slotIds.length; i += 1) {
      const idNumber = parseInt(slotIds[i].replace('cs', ''));
      if (idNumber < idsTaken.length) {
        idsTaken[idNumber] = true;
      }
    }

    let slotIdNumber = 1;
    while (idsTaken[slotIdNumber]) {
      slotIdNumber += 1;
    }

    return `cs${slotIdNumber}`;
  }

  addCharSlot(charId: CharSlotId, charMetadata: ICharMetadata): StoryTeller {
    if (this.slotIds.length === storyConfig.maxChars) {
      console.error(
        `Error: Cannot add more than ${storyConfig.maxChars} characters. Skipping...`,
      );
      return this;
    }
    const prevCharSlots = copy(this.story.charSlots);

    const config = getSlotDefaultConfig(charId);

    this.story.charSlots[charId] = {
      ...config,
      displayName: undefined,
      charMetadata,
      overrideable: undefined,
    };

    this.storyEventHandler(
      StoryEvents.SLOTS_UPDATE,
      getDiff(prevCharSlots, this.story.charSlots),
    );

    return this;
  }

  removeCharSlot(charId: CharSlotId): StoryTeller {
    // delete character slots first
    // @note: step removal triggers gesture updates which can lead to errors if the slots are not updated first
    const prevCharSlots = copy(this.story.charSlots);
    delete this.story.charSlots[charId];

    this.storyEventHandler(
      StoryEvents.SLOTS_UPDATE,
      getDiff(prevCharSlots, this.story.charSlots),
    );

    // delete gesture second

    // the gesture is deleted from every timeline
    const timelineIds = this.timelineIds;
    timelineIds.forEach((timelineId) => {
      if (timelineId === this.timelineId) {
        const steps = this._steps;
        let step = steps.s1;
        while (step) {
          step.removeGesture(charId);

          if (step.nextStep === undefined) {
            break;
          }
          step = steps[step.nextStep];
        }

        this.setSteps(steps);
        return;
      }

      const timeline = this.story.timelines[timelineId];
      const steps = timeline.steps;

      let step = steps.s1;
      while (step) {
        delete step.gestures[charId];

        if (step.nextStep === undefined) {
          break;
        }
        step = steps[step.nextStep];
      }
    });

    return this;
  }

  updateCharSlot(id: CharSlotId, charMetadata: ICharMetadata): StoryTeller {
    if (!this.story.charSlots[id]) {
      throw new Error(
        `Error (updateChar): Could not find slot with id '${id}'.`,
      );
    }
    const prevCharSlots = copy(this.story.charSlots);
    this.story.charSlots[id].charMetadata = charMetadata;

    this.storyEventHandler(
      StoryEvents.SLOTS_UPDATE,
      getDiff(prevCharSlots, this.story.charSlots),
    );
    return this;
  }

  // @TODO: Not sure if this should belong here
  // addCharacterFromMetadata(metadataUrl: string): Promise<StoryTeller> {}

  private onAddChoiceOption(): TimelineId {
    // Create choice timeline
    const choiceTimelineId: TimelineId = this.addTimeline();

    // Add copy of current step as first step in new timeline
    const stepCopy = this.step.getCopy();
    stepCopy.nextStep = undefined;
    stepCopy.prevStep = undefined;
    stepCopy.choices = undefined;
    stepCopy.camera = StepClass.getCamera({
      ...stepCopy.camera,
      motions: undefined,
    });

    this.story.timelines[choiceTimelineId].steps.s1 = stepCopy;

    // Set choice timeline toTimeline to merge timeline
    this.story.timelines[choiceTimelineId].toTimeline =
      this.story.timelines[this.timelineId].mergeTimeline;

    if (this.step.choiceIds.length !== 0) {
      return choiceTimelineId;
    }

    // Create merge timeline
    const mergeTimeline = this.addTimeline();
    this.story.timelines[mergeTimeline].steps.s1 = stepCopy;

    // Set mergeTimeline in our timeline's root timeline
    this.timeline.mergeTimeline = mergeTimeline;

    // Set choice timeline toTimeline to merge timeline
    this.story.timelines[choiceTimelineId].toTimeline = mergeTimeline;

    // Remove all steps after current
    const stepsToRemove: StepId[] = [];

    // From the last step to the first until we find ours, dispose anything before us
    for (let i = this.stepIds.length - 1; i > 0; i--) {
      const id = this.stepIds[i];
      if (id === this.stepId) {
        break;
      }

      stepsToRemove.push(id);
    }

    stepsToRemove.forEach((id) => this.removeStepById(id));

    return choiceTimelineId;
  }

  private onRemoveChoiceOption(id: ChoiceId): StoryTeller {
    if (!this.step.choices) {
      console.error(
        `Error (removeChoice): Step does not have choices. Skipping...`,
      );
      return this;
    }
    if (!this.step.choices[id]) {
      console.error(
        `Error (removeChoice): Could not find choice for id '${id}'. Skipping...`,
      );
      return this;
    }

    // If we are removing the last choice left, then remove the merge timeline as well
    if (this.step.choiceIds.length === 1) {
      const choiceTimeline =
        this.story.timelines[this.step.choices[id].toTimeline];
      if (choiceTimeline.toTimeline) {
        this.removeTimeline(choiceTimeline.toTimeline);
        this.timeline.mergeTimeline = undefined;
      } else {
        console.error(
          `Error (removeChoice): Something unexpected happened. We always expect choiceTimline to have toTimeline. Investigate`,
        );
        return this;
      }
    }

    this.removeTimeline(this.step.choices[id].toTimeline);

    return this;
  }

  removeTimeline(id: TimelineId): StoryTeller {
    if (id === INITIAL_TIMELINE) {
      console.error(
        `Error (removeTimeline): Cannot remove root timeline. Skipping...`,
      );
      return this;
    }
    const timeline = this.story.timelines[id];
    if (!timeline) {
      console.error(
        `Error (removeTimeline): Could not find timeline with id '${id}'. Skipping...`,
      );
      return this;
    }
    if (this.timeline === timeline) {
      console.error(
        `Error (removeTimeline): Cannot remove currently selected timeline. Skipping...`,
      );
      return this;
    }

    const choices = Object.values(timeline.steps).find(
      (s) => s.choices,
    )?.choices;

    if (choices) {
      Object.values(choices).forEach((c) => this.removeTimeline(c.toTimeline));
    }

    const hasLinks = Object.values(this.story.timelines).some(
      (t) => t !== timeline && t.toTimeline === timeline.toTimeline,
    );

    if (timeline.toTimeline && !hasLinks) {
      this.removeTimeline(timeline.toTimeline);
    }

    delete this.story.timelines[id];

    return this;
  }

  addStep(): StoryTeller {
    // This should ONLY happen when creating a story from scratch
    if (!this.step) {
      const step = this.getStep(this.stepId, {
        prevStep: undefined,
        nextStep: undefined,
        parallax: undefined,
        camera: undefined,
        bubble: undefined,
        choices: undefined,
        gestures: {},
      });

      this._steps[this.stepId] = step;
      this.timeline.steps[this.stepId] = step.getCopy();

      return this;
    }

    return this.addStepAfter(this.stepId);
  }

  private addStepAfter(stepId: StepId): StoryTeller {
    // This can ONLY happen on a new story if it's called before 'addStep'
    if (!this.firstStep) {
      throw new Error(`Error (addStepAfter): Could not find firstStep`);
    }
    if (this.steps[stepId].choices) {
      console.error(
        `Error (addStepAfter): Cannot add step after step with choices. Skipping...`,
      );
      return this;
    }

    let isBefore = true;

    const newSteps: Steps = {};

    this.stepIds.forEach((id) => {
      const step = this.steps[id];

      if (isBefore) {
        newSteps[id] = step;
      } else {
        const newId = increaseId<StepId>(id);
        newSteps[newId] = this.getStep(newId, {
          ...step.getCopy(),
          prevStep: id,
          nextStep:
            this.lastStepId === id ? undefined : increaseId<StepId>(newId),
        });
      }
      // Insert the new step as copy of the current
      if (id === stepId) {
        isBefore = false;
        const newId = increaseId<StepId>(id);
        newSteps[id] = this.getStep(id, {
          ...newSteps[id].getCopy(),
          nextStep: newId,
        });
        // Inserting Step
        newSteps[newId] = this.getStep(newId, {
          ...step.getCopy(),
          prevStep: id,
          nextStep:
            this.lastStepId === id ? undefined : increaseId<StepId>(newId),
        });
      }
    });

    this.setSteps(newSteps);
    this.timeline.lastStep = this.lastStepId;

    return this;
  }

  getStepGesture(
    charId: CharSlotId,
    givenStep?: IStep,
  ): Readonly<IGesture> | undefined {
    if (!givenStep) {
      givenStep = this.step;
    }

    // search for gesture in current timeline only
    let step = givenStep;
    while (step) {
      const gesture = step.gestures[charId];
      if (gesture) {
        return copy(gesture);
      }

      if (step.prevStep === undefined) {
        break;
      }
      step = this.steps[step.prevStep];
    }

    return;
  }

  removeStep(): StoryTeller {
    console.info(`Info (removeStep): Removing step '${this.stepId}'`);
    return this.removeStepById(this.stepId);
  }

  private removeStepById(stepId: StepId): StoryTeller {
    if (this.stepIds.length === 1) {
      console.error(
        `Error (removeStepById): Cannot remove all steps. Skipping...`,
      );
      return this;
    }

    if (!this.steps[stepId]) {
      console.error(
        `Error (removeStepById): Step id '${stepId}' not found in steps. Skipping...`,
      );
      return this;
    }

    // If we are deleting a step with choices we need to delete all timelines related to it
    const choices = this.steps[stepId].choices;
    if (choices) {
      Object.values(choices).forEach((c) => {
        this.removeTimeline(c.toTimeline);
      });
    }

    const newSteps: Steps = {};

    let isBefore = true;

    this.stepIds.forEach((id) => {
      const step = this.steps[id];

      if (id === stepId) {
        isBefore = false;
        if (id === this.lastStepId) {
          const newId = decreaseId<StepId>(id);
          newSteps[newId] = this.getStep(newId, {
            ...newSteps[decreaseId<StepId>(id)].getCopy(),
            nextStep: undefined,
          });
        }
        return;
      }

      if (isBefore) {
        newSteps[id] = step;
      } else {
        const newId = decreaseId<StepId>(id);
        newSteps[newId] = this.getStep(newId, {
          ...step.getCopy(),
          prevStep:
            newId === INITIAL_STEP ? undefined : decreaseId<StepId>(newId),
          nextStep: id === this.lastStepId ? undefined : id,
        });
      }
    });

    const index =
      this.linearStepIndex - (this.step.id === this.lastStep ? 1 : 0);

    this.setSteps(newSteps);
    this.timeline.lastStep = this.lastStepId;
    this.stepId = this.stepIds[index];

    this.storyEventHandler(StoryEvents.STEP_NAVIGATION);

    return this;
  }

  private onStepUpdate(step: StepClass) {
    this._steps[step.id] = step;
    this.timeline.steps[step.id] = step.getCopy();
  }

  private setSteps(steps: Steps) {
    this._steps = steps;
    this.timeline.steps = this.stepIds.reduce(
      (res, cur) => ({
        ...res,
        [cur]: steps[cur].getCopy(),
      }),
      {} as ISteps,
    );
  }

  private addTimeline(): TimelineId {
    const id: TimelineId = increaseId<TimelineId>(this.lastTimelineId);

    // If timeline already exists then something is wrong (maybe with our assumptions)
    if (this.story.timelines[id]) {
      throw new Error(
        `Error (addTimeline): Timeline already exists for id '${id}'.`,
      );
    }

    // @TODO: We might want to have a default step
    this.story.timelines[id] = {
      root: this.timelineId,
      firstStep: INITIAL_STEP,
      lastStep: INITIAL_STEP,
      steps: {},
      toTimeline: undefined,
      mergeTimeline: undefined,
    };

    return id;
  }

  getDefaultStory = (): IStory => {
    return {
      filterPreset: 'none',
      charSlots: {},
      metadata: {
        timestamp: Date.now(),
        version: lastMigrationVersion,
        saveCount: 0,
        editedBy: 'migrator',
        templateIndex: undefined,
        revealSchedule: undefined,
      },
      rootTimelineId: INITIAL_TIMELINE,
      timelines: {
        [INITIAL_TIMELINE]: {
          root: INITIAL_TIMELINE,
          firstStep: INITIAL_STEP,
          lastStep: INITIAL_STEP,
          steps: {},
          toTimeline: undefined,
          mergeTimeline: undefined,
        },
      },
      revealedTimelineId: undefined,
      bookmarks: undefined,
      choicesMade: {},
    };
  };

  private getStep(stepId: StepId, step: Partial<IStep>): StepClass {
    return new StepClass(
      stepId,
      {
        prevStep: step.prevStep,
        nextStep: step.nextStep,
        parallax: step.parallax,
        camera: step.camera,
        bubble: step.bubble,
        choices: step.choices,
        gestures: step.gestures || {},
      },
      {
        handleAddChoiceOption: () => this.onAddChoiceOption(),
        handleRemoveChoiceOption: (id: ChoiceId) => {
          this.onRemoveChoiceOption(id);
        },
        onUpdate: (evt: StepEvents, step: StepClass) => {
          this.stepEventHandler(evt, step);
        },
      },
    );
  }

  private addEventHandler(
    evt: StepEvents | StoryEvents,
    fn: (p: any) => void,
  ): DetachListener {
    if (!this.eventHandlers[evt]) {
      this.eventHandlers[evt] = [];
    }
    const id = Date.now();
    this.eventHandlers[evt]!.push({ id, fn });
    return {
      detach: () => {
        this.eventHandlers[evt] = this.eventHandlers[evt]!.filter(
          (ev) => ev.id !== id,
        );
      },
    };
  }

  // Events
  onBubbleUpdate(fn: (value: Diff) => void): DetachListener {
    return this.addEventHandler(StepEvents.BUBBLE_UPDATE, fn);
  }

  onGesturesUpdate(fn: (value: Diff) => void): DetachListener {
    return this.addEventHandler(StepEvents.GESTURES_UPDATE, fn);
  }

  onCameraUpdate(fn: (value: Diff) => void): DetachListener {
    return this.addEventHandler(StepEvents.CAMERA_UPDATE, fn);
  }

  onBackgroundUpdate(fn: (value: Diff) => void): DetachListener {
    return this.addEventHandler(StepEvents.BACKGROUND_UPDATE, fn);
  }

  onChoicesUpdate(fn: (value: Diff) => void): DetachListener {
    return this.addEventHandler(StepEvents.CHOICES_UPDATE, fn);
  }

  onSlotsChange(fn: (tlr: Diff) => void): DetachListener {
    return this.addEventHandler(StoryEvents.SLOTS_UPDATE, fn);
  }

  onFilterChange(fn: (tlr: Diff) => void): DetachListener {
    return this.addEventHandler(StoryEvents.FILTER_UPDATE, fn);
  }

  onTimelineChange(fn: (tlr: Diff) => void): DetachListener {
    return this.addEventHandler(StoryEvents.TIMELINE_NAVIGATION, fn);
  }

  onStepChange(fn: (tlr: Diff) => void): DetachListener {
    return this.addEventHandler(StoryEvents.STEP_NAVIGATION, fn);
  }

  private onStoryUpdate() {
    this._onStoryUpdate && this._onStoryUpdate(this.write());
  }

  private stepEventHandler(evt: StepEvents, step: StepClass, force = false) {
    const prevStep = this.step.pre;
    // update story data
    this.onStepUpdate(step);
    const newStep = this.step.getCopy();
    const handlers = this.eventHandlers[evt] || [];

    // Fire off event to listeners
    handlers.forEach((handler) => {
      const v1 = prevStep[evt];
      const v2 = newStep[evt];
      const diff = getDiff(v1, v2);
      if (force || diff.diff) {
        handler.fn(diff);
      }
    });

    this.onStoryUpdate();
  }

  private storyEventHandler(evt: StoryEvents, diff?: Diff) {
    const handlers = this.eventHandlers[evt] || [];

    // Fire off event to listeners
    handlers.forEach((handler) => {
      handler.fn(diff);
    });

    if (evt === StoryEvents.STEP_NAVIGATION) {
      this.stepEventHandler(StepEvents.BUBBLE_UPDATE, this.step, true);
      this.stepEventHandler(StepEvents.GESTURES_UPDATE, this.step, true);
      this.stepEventHandler(StepEvents.CAMERA_UPDATE, this.step, true);
      this.stepEventHandler(StepEvents.BACKGROUND_UPDATE, this.step, true);
      this.stepEventHandler(StepEvents.CHOICES_UPDATE, this.step, true);
    }

    this.onStoryUpdate();
  }

  private updateSteps() {
    this._steps = (
      sortKeys(Object.keys(this.timeline.steps)) as StepId[]
    ).reduce((res, cur) => {
      return {
        ...res,
        [cur]: this.getStep(cur, this.timeline.steps[cur]),
      };
    }, {} as Steps);
    this.storyEventHandler(StoryEvents.STEP_NAVIGATION);
  }

  addBookmark(bm: IBookmark) {
    const existingBm = this.bookmarks.find(
      (b) =>
        (b.timelineId === bm.timelineId && b.stepId === bm.stepId) ||
        b.label === bm.label,
    );

    if (existingBm) {
      throw new Error(
        `Error (addBookmark): Cannot add bookmark with same label or with same timeline/step.`,
      );
    }

    this.story.bookmarks = [...this.bookmarks, bm];

    return this;
  }

  removeBookmark(param: string | { timelineId: TimelineId; stepId: StepId }) {
    this.story.bookmarks = this.bookmarks.filter((b) => {
      let remove = false;
      if (typeof param === 'string') {
        remove = b.label === param;
      } else {
        remove = b.timelineId === param.timelineId && b.stepId === param.stepId;
      }
      return !remove;
    });

    return this;
  }

  // IO
  read(story: IStory, props?: STProps): StoryTeller {
    this.story = story;
    // Should we assume read is a new story the teller reads therefore should set initial values again
    this._timelineId = props?.timelineId || INITIAL_TIMELINE;
    this.stepId = props?.stepId || INITIAL_STEP;
    this._selectedChoice = undefined;
    this.updateSteps();
    return this;
  }

  toString = () => JSON.stringify(this.story, null, 2);

  write() {
    return this.story;
  }

  // Should be used ONLY for testing purposes
  __DANGEROUSLY_SET_METADATA(metadata: IStoryMetadata) {
    console.warn(
      `This method should be STRICTLY used for testing purposes. Do not use it in runtime`,
    );
    this.story.metadata = metadata;
    return this;
  }
}

type UpdateBubble = Partial<IBubble> & {
  caption: string;
  layout: IBubble['layout'];
};

type UpdateGesture = Partial<IGesture> & { charSlotId: CharSlotId };

// Map to the key from IStep
export enum StepEvents {
  BUBBLE_UPDATE = 'bubble',
  CAMERA_UPDATE = 'camera',
  BACKGROUND_UPDATE = 'parallax',
  GESTURES_UPDATE = 'gestures',
  CHOICES_UPDATE = 'choices',
}

interface Props {
  handleAddChoiceOption: () => TimelineId;
  handleRemoveChoiceOption: (id: ChoiceId) => void;
  onUpdate: (evt: StepEvents, step: StepClass) => void;
}

class StepClass {
  private prevState: IStep;

  constructor(private _id: StepId, private step: IStep, private props: Props) {
    this.prevState = step;
  }

  get id() {
    return this._id;
  }

  get prevStep() {
    return this.step.prevStep;
  }

  get nextStep() {
    return this.step.nextStep;
  }

  get choices(): IChoices | undefined {
    return this.step.choices ? copy(this.step.choices) : undefined;
  }

  get choiceIds(): ChoiceId[] {
    if (!this.step.choices) {
      return [];
    }
    return sortKeys(Object.keys(this.step.choices)) as ChoiceId[];
  }

  get bgImage(): string | undefined {
    return this.step.parallax?.layers[0]?.image;
  }

  get activeCharId(): CharSlotId | undefined {
    return this.step.bubble?.charSlotId || this.step.camera?.charSlotId;
  }

  get bubble(): IBubble | undefined {
    return copy(this.step.bubble);
  }

  get camera(): ICamera | undefined {
    return copy(this.step.camera);
  }

  get parallax(): IBackground | undefined {
    return copy(this.step.parallax);
  }

  // @TODO: Ideally this shouldn't be exposed
  get gestures(): IGestures {
    return copyStrict(this.step.gestures || {});
  }

  get pre(): IStep {
    return this.prevState;
  }

  clone(): StepClass {
    return new StepClass(this._id, this.step, this.props);
  }

  getCopy(): IStep {
    return copyStrict(this.step);
  }

  updateCamera(cam?: Partial<ICamera>): StepClass {
    this.prevState = this.getCopy();
    this.step.camera =
      cam &&
      StepClass.getCamera({
        ...this.camera,
        ...cam,
      });
    return this.updateCallback(StepEvents.CAMERA_UPDATE);
  }

  updateBackground(bg?: Partial<IBackground>): StepClass {
    this.prevState = this.getCopy();
    this.step.parallax =
      bg &&
      StepClass.getBackground({
        ...this.step.parallax,
        ...bg,
      });
    return this.updateCallback(StepEvents.BACKGROUND_UPDATE);
  }

  updateBackgroundImage(image: string): StepClass {
    if (!this.step.parallax) {
      throw new Error(
        `Error (updateBackgroundImage): Trying to update image without parallax set.`,
      );
    }
    this.prevState = this.getCopy();
    const newBg = StepClass.getBackground(this.step.parallax, image);
    this.step.parallax = newBg;
    return this.updateCallback(StepEvents.BACKGROUND_UPDATE);
  }

  updateBubble(bubble?: Partial<IBubble>): StepClass {
    this.prevState = this.getCopy();
    if (!bubble) {
      this.step.bubble = undefined;
      return this.updateCallback(StepEvents.BUBBLE_UPDATE);
    }
    // If we are setting bubble
    if (!this.bubble) {
      if (!bubble.caption || !bubble.layout) {
        throw new Error(
          `Error (updateBubble): Cannot set bubble without 'caption' or 'layout'.`,
        );
      }
    }
    this.step.bubble = StepClass.getBubble({
      ...this.bubble,
      ...bubble,
    } as IBubble);
    return this.updateCallback(StepEvents.BUBBLE_UPDATE);
  }

  updateGesture(id: CharSlotId, gesture: UpdateGesture): StepClass {
    this.prevState = this.getCopy();
    if (!gesture.charSlotId) {
      throw new Error(
        `Error (updateGesture): Trying to update charSlotId '${id}' without 'charSlotId' in gesture.`,
      );
    }
    if (id !== gesture.charSlotId) {
      throw new Error(
        `Error (updateGesture): Trying to update charSlotId '${id}' with gesture using different id (${gesture.charSlotId}).`,
      );
    }
    const currentGesture = this.step.gestures[id];

    this.step.gestures = {
      ...this.step.gestures,
      [id]: StepClass.getGesture({
        ...currentGesture,
        ...gesture,
      }),
    };
    return this.updateCallback(StepEvents.GESTURES_UPDATE);
  }

  removeGesture(id: CharSlotId): StepClass {
    this.prevState = this.getCopy();

    delete this.step.gestures[id];

    return this.updateCallback(StepEvents.GESTURES_UPDATE);
  }

  updateChoice(id: ChoiceId, caption: string): StepClass {
    this.prevState = this.getCopy();
    if (!this.step.choices) {
      console.error(
        `Error (updateChocice): Current step does not have choices. Did you forget to add choice?`,
      );
      return this;
    }

    if (!this.step.choices[id]) {
      console.error(
        `Error (updateChocice): Could not find choice for id '${id}'. Did you forget to add choice?`,
      );
      return this;
    }

    this.step.choices[id].caption = caption;
    return this.updateCallback(StepEvents.CHOICES_UPDATE);
  }

  addChoice(): StepClass {
    this.prevState = this.getCopy();
    if (this.choiceIds.length === storyConfig.maxChoiceOptions) {
      console.error(
        `Error (addChoice): Choice limit (${storyConfig.maxChars}) reached. Skipping...`,
      );
      return this;
    }

    const toTimeline = this.props.handleAddChoiceOption();

    const index = this.choiceIds.length + 1;

    const newId: ChoiceId = `c${index}`;
    const caption = `Option ${index}`;

    if (!this.step.choices) {
      this.step.choices = {};
    }

    this.step.choices![newId] = {
      caption,
      toTimeline,
    };

    return this.updateCallback(StepEvents.CHOICES_UPDATE);
  }

  removeChoice(id: ChoiceId): StepClass {
    this.prevState = this.getCopy();
    if (!this.step.choices) {
      console.error(
        `Error (removeChoice): Step does not have choices. Skipping...`,
      );
      return this;
    }
    if (!this.step.choices[id]) {
      console.error(
        `Error (removeChoice): Could not find choice for id '${id}'. Skipping...`,
      );
      return this;
    }

    this.props.handleRemoveChoiceOption(id);

    delete this.step.choices[id];

    if (this.choiceIds.length === 0) {
      this.step.choices = undefined;
    }

    return this.updateCallback(StepEvents.CHOICES_UPDATE);
  }

  public static getCamera(cam: Partial<ICamera>): ICamera {
    return {
      charSlotId: cam.charSlotId,
      preset: cam.preset,
      motions: cam.motions,
    };
  }

  public static getBackgroundLayer(
    layer: Partial<IBgLayer> & { image: string },
  ): IBgLayer {
    return {
      image: layer.image,
      width: layer.width,
      height: layer.height,
      x: layer.x,
      y: layer.y,
    };
  }

  public static getBackground(
    bg: Partial<IBackground>,
    image?: string,
  ): IBackground {
    const layers = image
      ? (bg.layers || []).map((layer) => {
          return StepClass.getBackgroundLayer({ ...layer, image });
        })
      : bg.layers || [];

    return {
      width: bg.width || 1500,
      height: bg.height || 1500,
      y: bg.y || 0,
      layers,
    };
  }

  public static getBubble(bubble: UpdateBubble): IBubble {
    return {
      mode: bubble.mode,
      tagname: bubble.tagname,
      tagcolor: bubble.tagcolor,
      charSlotId: bubble.charSlotId,
      layout: bubble.layout,
      caption: bubble.caption,
    };
  }

  public static getGesture(gesture: UpdateGesture): IGesture {
    return {
      charSlotId: gesture.charSlotId,
      dirX: gesture.dirX,
      animation: gesture.animation,
      loop: gesture.loop,
      charPos: gesture.charPos,
      charMotion: gesture.charMotion,
      visible: gesture.visible,
    };
  }

  private updateCallback(evt: StepEvents): StepClass {
    this.props.onUpdate(evt, this);
    return this;
  }
}

/**
 * This is so we can export the type of Step without exporting the class
 * It won't work if try to use 'typeof Step', it must type of an instance of step
 */
const instance = new StoryTeller().addStep().step;
export type Step = typeof instance;
